<template>
  <base-draggable-modal
      name="base_document_report"
      max-height="80vh"
      width="70vw"
      :fullscreen="$vuetify.display.xsOnly"
      ref="base_document_report">
    <v-card rounded="xl">
      <base-modal>
        <template #header>
          <v-toolbar class="draggable_selector" flat color="secondary" density="compact">
            <v-toolbar-title class="text-primary">{{ record.name }}</v-toolbar-title>
          </v-toolbar>
        </template>
        <template #content>
           <v-card-text>
             <v-row>
               <v-col>
                 <h1 class="text-h3 text-primary">{{ record.name }}</h1>
                 <div class="sm-medium text-primary mt-3">{{ type }} > {{ subtype }}</div>
                 <span class="sm-medium text-primary">{{ $store.getters.translate('root_asset_name') }}: {{ record.root_asset_name }}
                   <template v-if="record.second_root_asset_id">, {{ record.second_root_asset_name }}</template>
                   <template v-if="record.third_root_asset_id">, {{ record.third_root_asset_name }}</template>
                   <template v-if="record.fourth_root_asset_id">, {{ record.fourth_root_asset_name }}</template>
                   <template v-if="record.fifth_root_asset_id">, {{ record.fifth_root_asset_name }}</template>
                 </span>
                 <span class="mx-1">•</span>
                 <span class="sm-medium text-primary">{{ record.afe_pef }}</span>
               </v-col>
             </v-row>
             <v-row class="mt-0">
               <v-col>
                 <v-checkbox style="float: left;" color="success" density="compact" hide-details v-model="hide_commissioned"/>
                 <span class="pl-3 sm-normal text-subtle" style="display: block; height: 40px; align-content: center;">{{ $store.getters.translate('hide_commissioned') }}</span>
               </v-col>
             </v-row>
             <v-row class="mt-0">
               <v-col>
                 <div class="sm-medium text-subtle">{{ $store.getters.translate("filter_by_tags").toUpperCase() }}</div>
                 <v-radio-group v-model="selected_stats_tag" class="mt-3" density="compact" hide-details inline>
                   <v-radio
                       density="compact"
                       color="success"
                       class="mr-2"
                       v-for="(key, index) in Object.keys(all_stats)"
                       :value="key"
                       :key="index"
                       hide-details
                       small>
                     <template #label>
                       <span class="sm-normal text-subtle">{{ $store.getters.translate(key) }}</span>
                     </template>
                   </v-radio>
                 </v-radio-group>
               </v-col>
             </v-row>
             <v-row class="mt-0">
               <v-col><span class="sm-medium text-subtle">{{ $store.getters.translate('progress') }}: {{ Math.floor((stats_rows.commissioned * 100) / stats_rows.amount) }}%</span></v-col>
               <v-col><span class="sm-medium text-subtle">{{ $store.getters.translate('nr_documents') }}: {{ stats_rows.amount }}</span></v-col>
               <v-col><span class="sm-medium text-subtle">{{ $store.getters.translate('nr_commissioned') }}: {{ stats_rows.commissioned }}</span></v-col>
             </v-row>
             <v-data-table
                 class="elevation-1 mt-5"
                 :headers="headers"
                 :items="documents_rows"
                 v-bind="footer_props"
                 :show-select="false">
               <template #body="{ items }">
                 <tr v-for="item in items" :key="item.id" class="sm-normal text-primary">
                   <td style="text-align: center; width: 140px;">
                     <v-icon v-if="item.commissioned" color="success" size="large" icon="mdi-check-all"/>
                     <v-icon v-else color="error" size="large" icon="mdi-close"/>
                   </td>
                   <td><a @click="$emit('edit', item);">{{ item.name }}</a></td>
                   <td>{{ $lodash.truncate(item.description, {length: 50}) }}</td>
                   <td v-if="type === 'Certificates' && subtype === 'Certificate'" style="width: 200px;">
                     <v-autocomplete
                         color="primary"
                         bg-color="white"
                         variant="underlined"
                         density="compact"
                         v-if="item.flange_status !== 'accepted' && canEditFlangeManagement"
                         v-model="item.flange_status"
                         :return-object="false"
                         item-value="value"
                         item-title="text"
                         :items="flange_statuses"
                         @update:model-value="updateFlangeStatus(item)"
                         single-line
                         hide-details
                         clearable/>
                     <span v-else>{{ item.flange_status }}</span>
                   </td>
                 </tr>
               </template>
             </v-data-table>
           </v-card-text>
        </template>
        <template #footer>
          <v-card-text>
            <v-row>
              <v-col>
                <base-action-button :width="'100%'" @action="closeModal('base_document_report')">
                  <template #prepend><PhX style="vertical-align: middle; margin-right: 5px;" size="20" /></template>
                  {{ $store.getters.translate("cancel") }}
                </base-action-button>
              </v-col>
              <v-col>
                <base-action-button :background="true" :width="'100%'" @action="downloadReport">
                  <template #prepend><PhDownloadSimple style="vertical-align: middle; margin-right: 5px;" size="20" /></template>
                  {{ $store.getters.translate("download") }}
                </base-action-button>
              </v-col>
            </v-row>
          </v-card-text>
        </template>
      </base-modal>
    </v-card>
  </base-draggable-modal>
</template>

<script>
import {useAbility} from "@casl/vue";
import helpFunctions from "../../plugins/helpFunctions";
import BaseModal from "./BaseModal";
import BaseDraggableModal from "./BaseDraggableModal";
import BaseActionButton from "./BaseActionButton";
import {PhDownloadSimple, PhX} from "@phosphor-icons/vue";

export default {
  components: {
    BaseActionButton,
    BaseModal,
    BaseDraggableModal,
    PhDownloadSimple, PhX,
  },
  setup() {
    const { can } = useAbility()
    return { can }
  },
  data() {
    return {
      page: helpFunctions.pages.reports,
      headers: [],
      flange_statuses: [{ value: "disassembled", text: this.$store.getters.translate("disassembled") },
        { value: "assembled", text: this.$store.getters.translate("assembled") },
        { value: "tightened", text: this.$store.getters.translate("tightened") },
        { value: "n_a", text: "N/A" }],
      record: {},
      type: '',
      subtype: '',
      all_stats: [],
      all_documents: [],
      stats_rows: [],
      documents_rows: [],
      selected_stats_tag: "with_tags",
      hide_commissioned: false,
      footer_props: helpFunctions.footer_props,
    };
  },
  methods: {
    ...helpFunctions.modal_functions,
    async load(item) {
      this.record = item;
      this.openModal('base_document_report');
      this.headers = this.getHeaders();
      let response = await helpFunctions.postCustomRoute(this.$toast, "project-report-by-type", { project_id: this.record.id, type: this.type, subtype: this.subtype }, false, true);
      this.all_stats = response.counters;
      this.all_documents = response.documents;
      this.selected_stats_tag = "with_tags";
      this.stats_rows = this.all_stats[this.selected_stats_tag];
      this.documents_rows = [];
      this.hide_commissioned = false;
      this.documents_rows = [...this.all_documents[this.selected_stats_tag]['not_commissioned'],
        ...this.all_documents[this.selected_stats_tag]['commissioned']];
    },
    async downloadReport() {
      let report_tags = "";
      if(this.selected_stats_tag === "with_tags"/* || this.selected_stats_tag === "all"*/) {
        report_tags = this.record.tags.join(", ");
      }
      else {
        report_tags = this.selected_stats_tag;
      }
      let file_name = this.record.name.replaceAll('"', '').replaceAll("'", "").replaceAll('\\', ' ');
      file_name = file_name.replaceAll('/', ' ').replaceAll('(', ' ').replaceAll(')', ' ');
      file_name = file_name.replaceAll('   ', ' ').replaceAll('  ', ' ');
      file_name = "Progress report " + this.subtype + " " + file_name + " " + report_tags + ".pdf";

      let response = await helpFunctions.getPDFFile(this.$toast, "pdf/generate-project-report", {
        project_id: this.record.id,
        selected_stats_tag: this.selected_stats_tag,
        report_tags: report_tags,
        file_name: file_name,
        template_id: 3,
        type: this.type,
        subtype: this.subtype,
        hide_commissioned: this.hide_commissioned
      });
      const blob = new Blob([response], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = file_name;
      link.click();
      URL.revokeObjectURL(link.href);
    },
    updateStats() {
      this.stats_rows = this.all_stats[this.selected_stats_tag];
      this.documents_rows = [];
      if(this.hide_commissioned) {
        this.documents_rows = this.all_documents[this.selected_stats_tag]['not_commissioned'];
      }
      else {
        this.documents_rows = [...this.all_documents[this.selected_stats_tag]['not_commissioned'],
          ...this.all_documents[this.selected_stats_tag]['commissioned']];
      }
    },
    async updateFlangeStatus(item) {
      await helpFunctions.postCustomRoute(this.$toast, "change-flange-status", { id: item.id, flange_status: item.flange_status }, true, true);
    },
    getHeaders() {
      let headers = [
        {
          title: this.$store.getters.translate("commissioned"),
          align: "center",
          sortable: true,
          value: "commissioned",
          width: "140px"
        },
        {
          title: this.$store.getters.translate("name"),
          align: "start",
          sortable: true,
          value: "name",
        },
        {
          title: this.$store.getters.translate("description"),
          align: "start",
          sortable: true,
          value: "description",
        },
      ];
      if(this.type === "Certificates" && this.subtype === "Certificate") {
        headers.push({
          title: this.$store.getters.translate("flange_status"),
          align: "center",
          sortable: true,
          value: "flange_status",
          width: "200px"
        });
      }
      return headers;
    },
  },
  watch: {
    selected_stats_tag: {
      handler() {
        this.updateStats();
      },
    },
    hide_commissioned: {
      handler() {
        this.updateStats();
      },
    },
  },
  computed: {
    canEditFlangeManagement() {
      return this.can("show", this.$createEntity("flangemanagement", { id: 0 }));
    },
  },
};
</script>